import {
  useSelectedNumberByVariableKey,
  useSelectedValueAttributeByKey,
  useSelectedValueByVariableKey,
} from '@k3/core'
import React from 'react'
import { Vector3 } from 'three'
import { Helper } from '../../classes/helper/helper.class'
import {
  attachedBar,
  detachedBar,
  roofTopBar,
} from '../../three/mesh/terraceCover/model/helper/verandaDimensions'
import { isForbiddenValues, isMaxValuesPerVariable } from './isForbiddenValues'
import { Keys } from './utlility/utils'
import { has } from 'cypress/types/lodash'

export type MeasuresContextType = ReturnType<typeof useMeasuresContext>
export const MeasuresContext = React.createContext<MeasuresContextType>(
  {} as any
)

export const useMeasures = () => {
  const context = React.useContext(MeasuresContext)
  return context
}

export const useMeasuresContext = () => {
  const typeVariable = useSelectedValueByVariableKey(Keys.Modell.Variable)
  const forbiddenTypAmount = isMaxValuesPerVariable(
    Keys.Modell.MaxValues,
    typeVariable
  )
  const isQubus =
    typeVariable?.key === isForbiddenValues(Keys.Modell.Values.Qubus) &&
    'qubus' === typeVariable?.key

  const verandaTyp = {
    typ: useSelectedValueByVariableKey(Keys.Modell.Variable)?.key,
    log: {
      width:
        (Helper.stringToNumbers(
          useSelectedValueAttributeByKey(
            Keys.Modell.Variable,
            Keys.Modell.Attributes.Log
          )?.value as string
        )[0] as number) / 100 || 0.12,
      depth:
        (Helper.stringToNumbers(
          useSelectedValueAttributeByKey(
            Keys.Modell.Variable,
            Keys.Modell.Attributes.Log
          )?.value as string
        )[1] as number) / 100 || 0.04,
    },
    bar: {
      width:
        (Helper.stringToNumbers(
          useSelectedValueAttributeByKey(
            Keys.Modell.Variable,
            Keys.Modell.Attributes.Bar
          )?.value as string
        )[0] as number) / 100 || 0.12,
      depth:
        (Helper.stringToNumbers(
          useSelectedValueAttributeByKey(
            Keys.Modell.Variable,
            Keys.Modell.Attributes.Bar
          )?.value as string
        )[1] as number) / 100 || 0.04,
    },
    roofBar: {
      width:
        (Helper.stringToNumbers(
          useSelectedValueAttributeByKey(
            Keys.Modell.Variable,
            Keys.Modell.Attributes.RoofBar
          )?.value as string
        )[0] as number) / 100 || 0.12,
      depth:
        (Helper.stringToNumbers(
          useSelectedValueAttributeByKey(
            Keys.Modell.Variable,
            Keys.Modell.Attributes.RoofBar
          )?.value as string
        )[1] as number) / 100 || 0.04,
    },
  }
  const verandaLogWidth =
    (Helper.stringToNumbers(
      useSelectedValueAttributeByKey(
        Keys.Modell.Variable,
        Keys.Modell.Attributes.Log
      )?.value as string
    )[0] as number) / 100 || 0.12

  const withInput =
    (useSelectedNumberByVariableKey(Keys.Width.Variable) as number) / 100 || 0
  const depth =
    (useSelectedNumberByVariableKey(Keys.Depth.Variable) as number) / 100.0 || 0
  const userAngle1 =
    (useSelectedNumberByVariableKey(Keys.Angle.Vatiable) as number) || 0
  const userFrontHeight =
    (useSelectedNumberByVariableKey(Keys.FrontHeight.Variable) as number) /
      100.0 || 0

  const userWidth = withInput - (isQubus ? verandaTyp.log.width : 0)

  const coverStrips = {
    x:
      (Helper.stringToNumbers(
        useSelectedValueAttributeByKey(
          Keys.Modell.Variable,
          Keys.Modell.Attributes.CoverStrips
        )?.value as string
      )[1] as number) / 100 || 0,
    y:
      (Helper.stringToNumbers(
        useSelectedValueAttributeByKey(
          Keys.Modell.Variable,
          Keys.Modell.Attributes.CoverStrips
        )?.value as string
      )[0] as number) / 100 || 0,
  }

  const attachedBarMekmal = {
    y:
      (Helper.stringToNumbers(
        useSelectedValueAttributeByKey('attachedBar', 'size')?.value as string
      )[0] as number) / 100 || 0,
    z:
      (Helper.stringToNumbers(
        useSelectedValueAttributeByKey('attachedBar', 'size')?.value as string
      )[1] as number) / 100 || 0,
  }

  const attachedBarAttribute = useSelectedValueAttributeByKey(
    Keys.Modell.Variable,
    'attachedBar'
  )?.value as string

  const attachedBarSize = {
    x: userWidth,
    y: attachedBarMekmal.y
      ? attachedBarMekmal.y
      : (Helper.stringToNumbers(attachedBarAttribute)[0] as number) / 100 || 0,
    z: attachedBarMekmal.z
      ? attachedBarMekmal.z
      : (Helper.stringToNumbers(attachedBarAttribute)[1] as number) / 100 || 0,
  }

  const hasAttachedBar = !!(attachedBarSize.y || attachedBarSize.z) as boolean
  const hasCoverStrips = !!(coverStrips.x || coverStrips.y) as boolean

  const coverStripSize = hasCoverStrips
    ? coverStrips
    : { x: verandaTyp.roofBar.depth, y: roofTopBar.height }

  const amplitude =
    (useSelectedValueAttributeByKey(Keys.Eindeckung.Variable, 'amplitude')
      ?.value as number) || 0
  const attachedSize =
    hasAttachedBar && !isQubus
      ? attachedBarSize
      : isQubus
      ? {
          x: 0,
          y: 0,
          z: 0,
        }
      : {
          x: userWidth,
          y:
            Helper.calculateTriangleSideLength(
              userAngle1,
              90,
              verandaTyp.roofBar.width +
                (hasCoverStrips ? coverStripSize.y : roofTopBar.height)
            ).c + (hasCoverStrips ? coverStripSize.y : roofTopBar.height),
          z: attachedBar.depth,
        }

  const userHeightOld =
    (useSelectedNumberByVariableKey(Keys.Height.Variable) as number) / 100.0 ||
    0
  const userHeight =
    userHeightOld -
    (isQubus
      ? 0
      : Helper.calculateTriangleSideLength(
          userAngle1,
          90,
          verandaTyp.roofBar.width +
            (hasCoverStrips ? coverStripSize.y : roofTopBar.height)
        ).c) +
    attachedSize.y
  const frontCalculate =
    (+(userHeight - Math.tan((userAngle1 * Math.PI) / 180) * depth).toFixed(
      6
    ) as number) || 0

  const frontHeight = isQubus
    ? userHeight
    : userFrontHeight
    ? userFrontHeight
    : ((+Math.ceil(frontCalculate * 10000.0).toFixed(6) as number) || 0) /
      10000.0

  const userAngle = userFrontHeight
    ? (Helper.calculateAngleBeta(
        userHeight - frontHeight,
        depth - attachedBar.depth
      ) as number)
    : userAngle1

  const angleCompensation = -(Math.tan((userAngle * Math.PI) / 180) * depth) / 2

  const polesNumber =
    (useSelectedNumberByVariableKey('polesNumber') as number) || 2

  const polesBacksideInput =
    (useSelectedNumberByVariableKey('polesNumberBackside') as number) || 2

  const polesBackside = polesBacksideInput || polesNumber

  const polesNumberBackside = polesBacksideInput <= 2 ? 2 : polesBackside

  const polesTopNumber =
    (useSelectedNumberByVariableKey('polesTopNumber') as number) || 2

  const attachmentInput = useSelectedValueByVariableKey('attachment')?.key || ''

  const attachment = {
    free: ((attachmentInput === 'freistehend') as boolean) || false,
    wall: ((attachmentInput === 'wandmontage') as boolean) || false,
  }

  const allignment = useSelectedValueByVariableKey('alignment')?.key || ''

  const isQuer = (allignment !== 'laengs') as boolean

  const hasStartpoint =
    (useSelectedValueAttributeByKey('attachment', 'startPoint')
      ?.value as number) || 0
  const startPoint =
    hasStartpoint !== null ? hasStartpoint : attachment.free ? 0.5 : 0.1

  const startPositionTyp =
    (useSelectedValueAttributeByKey(Keys.Modell.Variable, 'place')
      ?.value as string) || ''
  const startTyp = {
    x:
      (Helper.stringToV3(
        useSelectedValueAttributeByKey(Keys.Modell.Variable, 'place')
          ?.value as string
      )[0] as number) || 0,
    y:
      (Helper.stringToV3(
        useSelectedValueAttributeByKey(Keys.Modell.Variable, 'place')
          ?.value as string
      )[1] as number) || 0,
    z:
      (Helper.stringToV3(
        useSelectedValueAttributeByKey(Keys.Modell.Variable, 'place')
          ?.value as string
      )[2] as number) || 0,
  }

  const startVerandaPosition1 = [startTyp.x, startTyp.y, startTyp.z]

  const startVerandaPosition =
    startPositionTyp !== ''
      ? new Vector3(
          startVerandaPosition1[0],
          startVerandaPosition1[1],
          startVerandaPosition1[2] - startPoint
        )
      : new Vector3(0, 0, -startPoint)

  const housRotate =
    useSelectedValueAttributeByKey('housRotate', 'grad')?.value || 0

  const facadeInput =
    useSelectedValueByVariableKey(Keys.Facade.Variable)?.key || ''

  const facade = {
    putz: (facadeInput ===
      isForbiddenValues(Keys.Facade.Values.Putz)) as boolean,
    klinker: (facadeInput ===
      isForbiddenValues(Keys.Facade.Values.Klinker)) as boolean,
  }

  const defaultStaticLog = useSelectedValueByVariableKey('staticLogPos')

  const hasStaticLog = defaultStaticLog
    ? ((defaultStaticLog?.key !== '$noSelection') as boolean) || false
    : false

  const backSideStaticLog = useSelectedValueByVariableKey('staticLogBackside')
  const hasBacksideStaticlog = backSideStaticLog
    ? ((backSideStaticLog?.key !== '$noSelection') as boolean) || false
    : false

  const staticLogBacksideAttr = useSelectedValueAttributeByKey(
    'staticLogBackside',
    'size'
  )?.value as string

  const staticLogBackside = {
    width: userWidth,
    height:
      (Helper.stringToNumbers(staticLogBacksideAttr)[0] as number) / 100 || 0,
    depth:
      (Helper.stringToNumbers(staticLogBacksideAttr)[1] as number) / 100 || 0,
  }

  const staticSize =
    (((useSelectedValueAttributeByKey('staticLogPos', 'size')
      ?.value as number) / 100) as number) || 0

  const staticLogPosition = hasStaticLog && staticSize ? staticSize : 0

  const stringStaticDynamicLog = hasStaticLog

  const roofBarSize =
    Helper.calculateTriangleSideLength(userAngle, 90, verandaTyp.roofBar.width)
      .c + 0.01

  let roofBar = {
    typ: useSelectedValueByVariableKey('rinne')?.key,
    frontBar: {
      width: roofBarSize,
      depth: 0.04,
    },
  }

  const defaultSchwelle = {
    width:
      (Helper.stringToNumbers(
        useSelectedValueAttributeByKey('rinne', 'schwelle')?.value as string
      )[0] as number) / 100 || roofBarSize,
    depth:
      (Helper.stringToNumbers(
        useSelectedValueAttributeByKey('rinne', 'schwelle')?.value as string
      )[1] as number) / 100 || 0.02,
  }

  defaultSchwelle.width = defaultSchwelle.width
    ? defaultSchwelle.width
    : roofBarSize
  defaultSchwelle.depth = defaultSchwelle.depth ? defaultSchwelle.depth : 0.02

  roofBar.frontBar = {
    width:
      (Helper.stringToNumbers(
        useSelectedValueAttributeByKey('staticLog', 'schwelle')?.value as string
      )[0] as number) / 100 || roofBarSize,
    depth:
      (Helper.stringToNumbers(
        useSelectedValueAttributeByKey('staticLog', 'schwelle')?.value as string
      )[1] as number) / 100 || 0.02,
  }

  const staticLogInput = useSelectedValueByVariableKey('staticLog')?.key || ''

  const hasSchwelle = staticLogInput !== '$noSelection' || false

  const schwelleSize = {
    width: isQubus
      ? 0
      : hasSchwelle
      ? roofBar.frontBar.width
      : defaultSchwelle.width,
    depth: isQubus
      ? 0
      : hasSchwelle
      ? roofBar.frontBar.depth
      : defaultSchwelle.depth,
  }

  const frontBarTyp = useSelectedValueByVariableKey('pfostenart')?.key

  const frontBarLogTyp = useSelectedValueByVariableKey('rinne')?.key

  const eavesWidthTest =
    (useSelectedNumberByVariableKey(Keys.EavesWidth.Variable) as number) /
      100.0 || 0
  const eavesWidth = eavesWidthTest ? eavesWidthTest : 0
  const eavesVariableValue = useSelectedValueByVariableKey(
    Keys.Eaves.Variable
  )?.value
  const eaves = eavesVariableValue !== '0' ? true : (false as boolean)

  const eavesHeight =
    frontHeight +
    Helper.calculateTriangle(eavesWidth, userAngle, 90).b -
    (hasStaticLog
      ? staticSize + Helper.calculateTriangle(staticSize, userAngle, 90).b / 2
      : 0.1)

  const frontLogBarHeight = attachment.free
    ? userHeight - detachedBar.height / 2
    : frontHeight

  const hasDynamicBar =
    ((useSelectedValueByVariableKey(Keys.DynamicLog.Variable)?.key ===
      isForbiddenValues(Keys.DynamicLog.Values.Ja)) as boolean) || false

  const logsFrontIndent = useSelectedValueByVariableKey('logIndent')?.value || 0
  const leftLogPos =
    (useSelectedNumberByVariableKey('leftLogPos') as number) / 100 || 0
  const rightLogPos =
    (useSelectedNumberByVariableKey('rightLogPos') as number) / 100 || 0
  const indentBar = (logsFrontIndent as number)
    ? { left: leftLogPos, right: rightLogPos }
    : { left: 0, right: 0 }

  stringStaticDynamicLog
    ? (roofBar.frontBar.depth = schwelleSize.depth)
    : (roofBar.frontBar.depth = 0.04)

  const offsetRoofLog = Helper.calculateTriangleSideLength(
    userAngle,
    90,
    verandaTyp.roofBar.width +
      (hasCoverStrips ? coverStripSize.y : roofTopBar.height)
  )

  attachedSize.z =
    verandaTyp.log.depth > attachedSize.z && attachment.free
      ? verandaTyp.log.depth
      : attachedSize.z
  const result = Helper.calculateTriangle(
    depth - attachedSize.z - verandaTyp.log.depth - schwelleSize.depth + 0.02,
    90,
    userAngle
  )

  const roofLogWidth = isQubus
    ? result.b - (attachment.free ? 0 : verandaTyp.log.depth)
    : result.b + 0.02
  const abstandLed = (useSelectedNumberByVariableKey('abstandLed') ||
    2) as number

  const hasLedStripe = (useSelectedValueByVariableKey('ledStripe')?.key ===
    'ja' || false) as boolean

  return {
    userWidth,
    depth,
    userHeight,
    userAngle,
    angleCompensation,
    frontHeight,
    roofLogWidth,
    polesNumber,
    polesNumberBackside,
    polesTopNumber,
    attachment,
    facade,
    frontBarTyp,
    frontBarLogTyp,
    verandaTyp,
    eaves,
    eavesHeight,
    eavesWidth,
    offsetRoofLog,
    roofBar,
    frontLogBarHeight,
    hasDynamicBar,
    startPoint,
    startPositionTyp,
    startVerandaPosition,
    frontCalculate,
    indentBar,
    staticLogPosition,
    hasStaticLog,
    stringStaticDynamicLog,
    housRotate,
    isQuer,
    hasAttachedBar,
    attachedBarSize,
    coverStrips,
    hasCoverStrips,
    coverStripSize,
    attachedSize,
    isQubus,
    schwelleSize,
    verandaLogWidth,
    abstandLed,
    hasLedStripe,
    forbiddenTypAmount,
    staticLogBackside,
    hasBacksideStaticlog,
  }
}

export const MeasureContextProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const context = useMeasuresContext()

  return (
    <MeasuresContext.Provider value={context}>
      {children}
    </MeasuresContext.Provider>
  )
}
